import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Mail, TrendingUp, TrendingDown, Send, MousePointerClick, UserMinus, Target, Download, ArrowRight, Loader2, DollarSign, CheckCircle, Percent } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";
import { exportEmailPDF, filterByDateRange, calculateChange, monthYearToRange, availableYears, previousMonthReport, monthLabel } from "../utils/reportUtils";
import { MonthYearFilter } from "../components/MonthYearFilter";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// KPI Card Component with description
const KPICard = ({ title, value, description, change, icon: Icon, color = "blue", suffix = "", prefix = "", alert = false, lowerIsBetter = false }) => {
  const colorClasses = {
    blue: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-100" },
    green: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-100" },
    purple: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-100" },
    amber: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-100" },
    red: { bg: "bg-red-50", text: "text-red-600", border: "border-red-100" },
    orange: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-100" }
  };
  
  const colors = colorClasses[color];
  const isUp = change >= 0;
  const isGood = lowerIsBetter ? change < 0 : change >= 0;

  return (
    <Card className={`stat-card ${alert ? colors.border : ''}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-3 rounded-xl ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} strokeWidth={1.5} />
          </div>
          {change !== undefined && change !== null && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              change === 0 ? 'bg-slate-100 text-slate-500' : isGood ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`} title="Variação face ao mês anterior">
              {change !== 0 && (isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
              {isUp && change !== 0 ? "+" : ""}{change.toFixed(1)}%
            </div>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className={`text-3xl font-bold ${alert ? colors.text : 'text-slate-900'}`} style={{ fontFamily: 'Manrope' }}>
            {prefix}{value}{suffix}
          </p>
          <p className="text-xs text-slate-400 leading-relaxed mt-2">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const EmailReportsPage = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const { startDate, endDate } = monthYearToRange(selectedMonth, selectedYear);
  const years = availableYears(reports);

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    const filtered = filterByDateRange(reports, startDate, endDate);
    setFilteredReports(filtered);
  }, [reports, startDate, endDate]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/email`);
      setReports(response.data);
      setFilteredReports(response.data);
    } catch (error) {
      console.error("Error fetching Email reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (filteredReports.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }
    
    setExporting(true);
    try {
      const clientId = user?.client_id || filteredReports[0]?.client_id;
      if (!clientId) {
        toast.error("Cliente não identificado");
        return;
      }
      await exportEmailPDF(clientId, startDate, endDate);
      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar PDF:", error);
      toast.error("Erro ao exportar PDF: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSelectedMonth("all");
    setSelectedYear("all");
  };

  const latestReport = filteredReports[0];
  const previousReport = previousMonthReport(reports, latestReport);

  const pctChange = (field) => {
    if (!latestReport || !previousReport) return null;
    if (!latestReport[field] && !previousReport[field]) return null; // sem dados nos dois meses
    return calculateChange(latestReport[field], previousReport[field]);
  };
  const ctoRate = (r) => (r && r.open_rate > 0 ? (r.click_rate / r.open_rate) * 100 : 0);
  const ctoChange = latestReport && previousReport
    ? calculateChange(ctoRate(latestReport), ctoRate(previousReport))
    : null;

  const chartData = filteredReports.slice(0, 6).reverse().map(r => ({
    period: r.period_start.slice(5, 10),
    sent: r.emails_sent || 0,
    delivered: r.delivered || 0
  }));

  const performanceData = filteredReports.slice(0, 6).reverse().map(r => ({
    period: r.period_start.slice(5, 10),
    openRate: r.open_rate || 0,
    clickRate: r.click_rate || 0
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}><CardContent className="pt-6"><div className="skeleton h-32" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="email-reports-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <div className="p-2 bg-emerald-100 rounded-xl">
              <Mail className="w-7 h-7 text-emerald-600" />
            </div>
            Email Marketing
          </h1>
          <p className="text-slate-500 mt-2">
            Performance de campanhas de email e engagement da audiência
          </p>
        </div>
        
        {/* Filters & Export */}
        <div className="flex flex-wrap items-end gap-3">
          <MonthYearFilter
            month={selectedMonth}
            year={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            years={years}
          />
          {(selectedMonth !== "all" || selectedYear !== "all") && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              Limpar
            </Button>
          )}
          <Button 
            onClick={handleExportPDF} 
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-pdf-btn"
            disabled={exporting}
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exporting ? "A exportar..." : "Exportar PDF"}
          </Button>
        </div>
      </div>

      {/* Platform Badge */}
      {latestReport?.platform && (
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm px-3 py-1">
            <Mail className="w-3 h-3 mr-2" />
            Plataforma: {latestReport.platform}
          </Badge>
        </div>
      )}

      {/* Comparison info */}
      {latestReport && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="comparison-info">
          <ArrowRight className="w-4 h-4 text-emerald-600" />
          {previousReport ? (
            <span>
              A mostrar <span className="font-medium text-slate-700">{monthLabel(latestReport.period_start)}</span> — variação face a{" "}
              <span className="font-medium text-slate-700">{monthLabel(previousReport.period_start)}</span>
            </span>
          ) : (
            <span>A mostrar {monthLabel(latestReport.period_start)} — sem mês anterior para comparar</span>
          )}
        </div>
      )}

      {latestReport && (
        <>
          {/* Section: Delivery */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Envio e Entrega
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Emails Enviados"
                value={(latestReport.emails_sent || 0).toLocaleString('pt-PT')}
                description="Total de emails disparados nas campanhas durante o período."
                change={pctChange('emails_sent')}
                icon={Send}
                color="blue"
              />
              <KPICard
                title="Emails Entregues"
                value={(latestReport.delivered || 0).toLocaleString('pt-PT')}
                description="Emails que chegaram à caixa de entrada. A diferença são bounces."
                change={pctChange('delivered')}
                icon={CheckCircle}
                color="green"
              />
              <KPICard
                title="Taxa de Unsubscribe"
                value={(latestReport.unsubscribe_rate || 0).toFixed(2)}
                suffix="%"
                description="Percentagem de subscritores que cancelaram a subscrição. Deve ser inferior a 0.5%."
                change={pctChange('unsubscribe_rate')}
                lowerIsBetter
                icon={UserMinus}
                color="red"
                alert={true}
              />
            </div>
          </div>

          {/* Section: Engagement */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Engagement
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Open Rate"
                value={(latestReport.open_rate || 0).toFixed(1)}
                suffix="%"
                description="Percentagem de emails abertos. Benchmark B2B: 15-25%. Indica qualidade do assunto."
                change={pctChange('open_rate')}
                icon={Mail}
                color="green"
              />
              <KPICard
                title="Click Rate"
                value={(latestReport.click_rate || 0).toFixed(1)}
                suffix="%"
                description="Percentagem de cliques nos links. Benchmark: 2-5%. Indica relevância do conteúdo."
                change={pctChange('click_rate')}
                icon={MousePointerClick}
                color="purple"
              />
              <KPICard
                title="Click-to-Open Rate"
                value={latestReport.open_rate > 0 ? ((latestReport.click_rate / latestReport.open_rate) * 100).toFixed(1) : '0.0'}
                suffix="%"
                description="Percentagem de quem abriu e clicou. Indica qualidade do conteúdo do email."
                change={ctoChange}
                icon={Percent}
                color="amber"
              />
            </div>
          </div>

          {/* Section: Results */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Resultados
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <KPICard
                title="Conversões"
                value={(latestReport.conversions || 0).toLocaleString('pt-PT')}
                description="Ações completadas após interação com email (compras, registos, downloads, etc.)."
                change={pctChange('conversions')}
                icon={Target}
                color="green"
              />
              <KPICard
                title="Revenue Gerado"
                value={(latestReport.revenue || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
                suffix="€"
                description="Receita total atribuída às campanhas de email marketing."
                change={pctChange('revenue')}
                icon={DollarSign}
                color="green"
              />
            </div>
          </div>
        </>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Send className="w-5 h-5 text-blue-600" />
              Emails Enviados vs Entregues
            </CardTitle>
            <CardDescription>
              Volume de envios e taxa de entrega ao longo do tempo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="period" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="sent" fill="#2563EB" name="Enviados" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="delivered" fill="#10B981" name="Entregues" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                Sem dados disponíveis
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <MousePointerClick className="w-5 h-5 text-purple-600" />
              Open Rate vs Click Rate
            </CardTitle>
            <CardDescription>
              Evolução das métricas de engagement das campanhas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {performanceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={performanceData}>
                  <defs>
                    <linearGradient id="colorOpenRate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorClickRate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="period" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => `${value.toFixed(1)}%`}
                  />
                  <Area type="monotone" dataKey="openRate" stroke="#10B981" strokeWidth={2} fill="url(#colorOpenRate)" name="Open Rate" />
                  <Area type="monotone" dataKey="clickRate" stroke="#8B5CF6" strokeWidth={2} fill="url(#colorClickRate)" name="Click Rate" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                Sem dados disponíveis
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reports Table */}
      <Card className="stat-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              Histórico de Relatórios
            </CardTitle>
            <CardDescription>
              Todos os relatórios de Email Marketing registados
            </CardDescription>
          </div>
          <Badge variant="outline" className="badge-info">
            {filteredReports.length} relatório(s)
          </Badge>
        </CardHeader>
        <CardContent>
          {filteredReports.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Período</TableHead>
                    <TableHead>Plataforma</TableHead>
                    <TableHead className="text-right">Enviados</TableHead>
                    <TableHead className="text-right">Entregues</TableHead>
                    <TableHead className="text-right">Open Rate</TableHead>
                    <TableHead className="text-right">Click Rate</TableHead>
                    <TableHead className="text-right">Unsub</TableHead>
                    <TableHead className="text-right">Conv.</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReports.map((report) => (
                    <TableRow key={report.id} data-testid={`email-report-row-${report.id}`}>
                      <TableCell className="font-medium">
                        {report.period_start} a {report.period_end}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{report.platform || 'Brevo'}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.emails_sent || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.delivered || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="badge-success">
                          {(report.open_rate || 0).toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.click_rate || 0).toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {(report.unsubscribe_rate || 0).toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.conversions || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right text-emerald-600">
                        {(report.revenue || 0).toFixed(2)}€
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Sem relatórios Email Marketing para o período selecionado</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Key Insights */}
      {latestReport?.notes && (
        <Card className="stat-card bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              Key Insights
            </CardTitle>
            <CardDescription>Observações e recomendações do período</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 leading-relaxed">{latestReport.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmailReportsPage;
